import { getServiceNameFromServiceId } from '@axinom/mosaic-id-utils';
import type { FilterValues } from '@axinom/mosaic-ui';
import { useEffect, useMemo, useState } from 'react';
import type {
  ActivePermission,
  Permission,
} from '../../../../../generated/id-service-access-management';
import type {
  PermissionChild,
  PermissionData,
  PermissionFilters,
  PermissionFormData,
  PermissionMap,
  PermissionService,
} from './models';

export type PermissionList = Pick<
  ActivePermission,
  'id' | 'title' | 'fromManagedService' | 'serviceId'
>[];
/**
 * Maps all permissions to services and holds the original state
 * @param list all available permissions
 */
export const usePermissionList = (
  list: PermissionList,
  currentPermissions?: PermissionData[],
): {
  readonly permissionMap: PermissionMap[];
  readonly formData: PermissionFormData;
} => {
  const [permissionMap, setPermissionMap] = useState<PermissionMap[]>([]);
  const [formData, setFormData] = useState<PermissionFormData>({});

  useEffect(() => {
    if (list.length > 0 && currentPermissions !== undefined) {
      setPermissionMap(mapServicePermissions(list as Permission[]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list, currentPermissions]);

  const mapServicePermissions = (nodes: Permission[] = []): PermissionMap[] => {
    const serviceMap: { [serviceId: string]: PermissionService } = {};

    const formDataMap: PermissionFormData = {};

    // find all permissions currently assigned on the server
    const assignedOnServer: {
      [permissionId: string]: {
        assignmentId: string;
        isInherited: boolean | null | undefined;
      };
    } = {};
    for (const assignment of currentPermissions as PermissionData[]) {
      assignedOnServer[assignment.permissionId] = {
        assignmentId: assignment.id,
        isInherited: assignment.isPermissionInherited,
      };
    }

    // loop through all permissions
    for (const node of nodes) {
      if (serviceMap[node.serviceId] === undefined) {
        // Creating a service the first time we see a permission of it
        serviceMap[node.serviceId] = {
          id: node.serviceId,
          label: createServiceLabel(node.serviceId, node.fromManagedService),
          isManagedService: node.fromManagedService,
          children: [],
        };
      }

      // Adding the permission to the service
      serviceMap[node.serviceId].children.push({
        permission: node.name,
        id: node.id,
        name: node.name,
        label: node.title,
        isManaged: node.fromManagedService,
      });

      // format permissions and map currently assigned permissions for form use
      const assignmentId = assignedOnServer[node.id];
      formDataMap[node.id] = {
        ...(assignmentId !== undefined
          ? {
              assignmentId: assignmentId.assignmentId,
              isInherited: assignmentId.isInherited,
            }
          : {}),
        isAssigned: assignmentId !== undefined,
      };
    }
    setFormData(formDataMap);

    const tempMap: PermissionMap[] = [];

    for (const service in serviceMap) {
      // Pushing the services to an array
      tempMap.push({ service: serviceMap[service] });
      // Sort the permissions by name
      serviceMap[service].children.sort(
        (a: PermissionChild, b: PermissionChild) => {
          return a.label > b.label ? 1 : -1;
        },
      );
    }

    // Separate managed and non-managed services for sorting
    const managedServices = tempMap
      .filter((service) => service.service.isManagedService)
      .sort((a, b) => {
        return a.service.label > b.service.label ? 1 : -1;
      });

    const nonManagedServices = tempMap
      .filter((service) => !service.service.isManagedService)
      .sort((a, b) => {
        return a.service.label > b.service.label ? 1 : -1;
      });

    return [...managedServices, ...nonManagedServices];
  };

  return { permissionMap, formData } as const;
};

/**
 * Handles permission filtering
 * @param initialPermissions initial permission map
 * @param formData form data with current permission assignments
 * @param filters filters to be applied
 */
export const useFilters = (
  initialPermissions: PermissionMap[],
  formData: PermissionFormData,
  filters: FilterValues<PermissionFilters>,
): {
  readonly filteredPermissions: PermissionMap[];
} => {
  const filteredPermissions = useMemo(() => {
    let permissions: PermissionMap[] = [];

    if (Object.keys(filters).length > 0) {
      // loop through all services
      for (const service of initialPermissions) {
        const filteredChildren: PermissionChild[] = [];
        const filteredService: PermissionMap = {
          service: { ...service.service, children: filteredChildren },
        };
        // loop through all permissions in the current service
        for (const child of service.service.children) {
          const formDataChild = formData[child.id];
          let passPermissionName = true;
          let passIsAssigned = true;
          let passIsManaged = true;
          let passIsInherited = true;

          // filter assigned permissions
          if (filters.isAssigned === 'OFF' && formDataChild.isAssigned) {
            passIsAssigned = false;
          } else if (filters.isAssigned === 'ON' && !formDataChild.isAssigned) {
            passIsAssigned = false;
          }

          // filter managed permissions
          if (filters.isManaged === 'FALSE' && child.isManaged) {
            passIsManaged = false;
          } else if (filters.isManaged === 'TRUE' && !child.isManaged) {
            passIsManaged = false;
          }

          // filter inherited permissions, No back end support yet for this.
          if (
            filters.isInherited === 'FALSE' &&
            formDataChild.isInherited === true
          ) {
            passIsInherited = false;
          } else if (
            filters.isInherited === 'TRUE' &&
            formDataChild.isInherited !== true
          ) {
            passIsInherited = false;
          }

          // filer permission Title
          if (filters.permission !== undefined) {
            const searchTerm = filters.permission as string;
            const searchString = child.label.toLowerCase();
            const pos = searchString.search(searchTerm.toLowerCase()) || 0;
            if (pos < 0) {
              passPermissionName = false;
            }
          }

          // only return node that passes all filter checks
          if (
            passPermissionName &&
            passIsAssigned &&
            passIsManaged &&
            passIsInherited
          ) {
            filteredChildren.push(child);
          }
        }

        // only push a service into results if there are permissions to be shown
        if (filteredChildren.length > 0) {
          permissions.push(filteredService);
        }
      }
    } else {
      permissions = initialPermissions;
    }
    return permissions;
  }, [filters, formData, initialPermissions]);

  return { filteredPermissions } as const;
};

/**
 * Removes dashes in a string and returns it in uppercase along with an optional flag
 * Appends a flag if isManaged is true
 * @param text string to be transformed
 * @param isManaged whether or not the service has any managed permissions
 */
const createServiceLabel = (text: string, isManaged?: boolean): string => {
  const label: string = transformServiceIds(text, isManaged)
    .split('-')
    .map((word) => {
      return word.toUpperCase();
    })
    .join(' ');
  const managedFlag: string = isManaged === true ? ' (Managed by Axinom)' : '';
  return `${label}${managedFlag}`;
};

/**
 * Transforms known managed-service-ids to corresponding display texts.
 * For all else, returns the input service-id as the output.
 * @param serviceId managed-service-id to be transformed
 * @param isManaged whether or not the service has any managed permissions
 */
const transformServiceIds = (
  serviceId: string,
  isManaged?: boolean,
): string => {
  if (isManaged) {
    const serviceName = getServiceNameFromServiceId(serviceId, true);
    if (serviceName === undefined) {
      return serviceId;
    }
    return serviceName;
  } else {
    return serviceId;
  }
};
